import React, { createContext, useContext } from 'react';
import PropTypes from 'prop-types';
import { Dimmer, Loader, Table, Header, Icon } from 'semantic-ui-react';
import { getOrdinalValueWithSuffix } from './Table/utility';
import { FlexCard } from './';
import { black } from './utilities';

import styled from 'styled-components';

// **********************************
// Higher Order Component - Wrapper
// **********************************

export const PageLoaderWithMessagesHandler = ({
  loaderMessage,
  isLoading,
  topic,
  noDataFound,
  noDataFoundSettings,
  children,
}) => {
  return isLoading ? (
    <PageLoaderWithMessages
      loaderMessage={loaderMessage}
      isLoading={isLoading}
      topic={topic}
    />
  ) : noDataFound ? (
    <NoDataMessage
      topic={noDataFoundSettings?.topic || topic}
      icon={noDataFoundSettings?.icon}
      primaryMessage={noDataFoundSettings?.primaryMessage}
      secondaryMessage={noDataFoundSettings?.secondaryMessage}
      tertiaryMessage={noDataFoundSettings?.tertiaryMessage}
    />
  ) : (
    children
  );
};

export const PageLoaderHandler = ({ isLoading, children }) => {
  // console.log('isLoading', isLoading);
  return isLoading ? <PageLoader isLoading={isLoading} /> : children;
};

// **********************************
// Loaders
// **********************************

export const TableDataLoader = ({
  loaderMessage = 'Please wait...',
  columnCount,
  isLoading,
  pageSize,
  activePage,
  topic = 'data',
  isClearing = false,
}) => {
  const secondaryMessage = isClearing
    ? `While we clear all the search criteria 😃`
    : `While we fetch and load the ${getOrdinalValueWithSuffix(
        activePage,
      )} page with ${pageSize} rows of ${topic?.toLowerCase()} 😃`;

  return (
    <Table.Body>
      <Table.Row>
        <Table.Cell colSpan={columnCount}>
          <Dimmer.Dimmable as={StyledFlexCard} blurred dimmed={isLoading}>
            <Dimmer active={isLoading} inverted>
              <Loader inline size="massive">
                <Header as="h2">{loaderMessage}</Header>
                <Header as="h3" content={secondaryMessage} />
              </Loader>
            </Dimmer>
          </Dimmer.Dimmable>
        </Table.Cell>
      </Table.Row>
    </Table.Body>
  );
};

export const StyledFlexCard = styled(FlexCard)`
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 50vh;
  && ${Dimmer} {
    width: 100vw;
    height: 50vh;
  }
`;

export const PageLoaderWithMessages = ({
  loaderMessage = 'Please wait...',
  isLoading,
  topic = 'data',
}) => {
  const secondaryMessage = `While we fetch and load ${topic?.toLowerCase()} 😃`;

  return (
    <Dimmer.Dimmable as={StyledFlexCard} blurred dimmed={isLoading}>
      <Dimmer active={isLoading} inverted>
        <Loader inline size="massive">
          <Header as="h1">{loaderMessage}</Header>
          <Header as="h2" content={secondaryMessage} />
        </Loader>
      </Dimmer>
    </Dimmer.Dimmable>
  );
};

export const PageLoader = ({ isLoading, loadingText }) => (
  <Dimmer active={isLoading} inverted>
    <Loader size="massive" content={loadingText} />
  </Dimmer>
);

PageLoader.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  loadingText: PropTypes.string,
};

PageLoader.defaultProps = {
  loadingText: 'Loading...',
};

// **********************************
// Loader context
// **********************************

export const LoaderContext = createContext({ isLoading: true });

export const LoaderProvider = ({ value, children }) => {
  const { isLoading } = value;
  return (
    <LoaderContext.Provider value={{ isLoading }}>
      {children}
    </LoaderContext.Provider>
  );
};

export const useLoader = () => useContext(LoaderContext);

export const NoDataMessage = ({
  icon = { name: 'search', color: black },
  primaryMessage = {
    message: 'Oops, it appears we were not able to find any [topic] 🤔',
    color: black,
  },
  secondaryMessage = { message: 'Please try again!', color: black },
  tertiaryMessage = null,
  topic,
}) => {
  return (
    <FlexCard
      direction="column"
      style={{ height: '50vh' }}
      verticalAlignment="center"
      horizontalAlignment="center"
    >
      <Header as="h2" icon style={{ color: primaryMessage.color }}>
        <Icon name={icon.name} style={{ color: icon.color }} />
        {primaryMessage.message.replace(
          '[topic]',
          topic?.toLowerCase() ?? 'data',
        )}
      </Header>
      <Header
        as="h3"
        content={secondaryMessage.message}
        style={{ color: secondaryMessage.color }}
      />
      {tertiaryMessage && (
        <Header
          as="h4"
          content={tertiaryMessage.message}
          style={{ color: tertiaryMessage.color }}
        />
      )}
    </FlexCard>
  );
};

const messagePropType = PropTypes.shape({
  message: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
});

NoDataMessage.propTypes = {
  icon: PropTypes.shape({
    name: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
  }),
  primaryMessage: messagePropType,
  secondaryMessage: messagePropType,
  tertiaryMessage: messagePropType,
  topic: PropTypes.string,
};
